import {
  Alert,
  Button,
  Chip,
  DataTable,
  type DataTableColumnDef,
  Group,
  Icon,
  Select,
  Skeleton,
  type SortingState,
  Stack,
  TableCellPrimary,
  TableCellSecondary,
  TableCellStack,
  Text,
  Title,
  Tooltip,
} from "@assetwatch/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  EnterpriseAssetsV2RequestOptions,
  EnterpriseAssetsV2SummaryResponse,
  EnterpriseAssetsV2TableResponse,
} from "../api/enterpriseClient";
import { useGetAssetsV2Table } from "../queries/useGetAssetsV2Table";
import { formatAssetsV2Count } from "../utils";
import { AssetsV2HighRiskAssetsTableSkeleton } from "./snowflakes/AssetsV2HighRiskAssetsTableSkeleton";

type AssetRow =
  EnterpriseAssetsV2TableResponse["highRiskAssets"]["rows"][number];
type RiskType = NonNullable<EnterpriseAssetsV2RequestOptions["riskType"]>;
type SortDirection = NonNullable<
  EnterpriseAssetsV2RequestOptions["sortDirection"]
>;
type SortBy = NonNullable<EnterpriseAssetsV2RequestOptions["sortBy"]>;

const ALL_FACILITIES_VALUE = "__all_facilities__";
const TABLE_PAGE_SIZE = 25;

export function AssetsV2HighRiskAssetsTable({
  customerId,
  summary,
  hasFacilityGroups,
  facilities,
}: {
  customerId: number;
  summary: EnterpriseAssetsV2SummaryResponse["summary"];
  hasFacilityGroups: boolean;
  facilities: EnterpriseAssetsV2SummaryResponse["facilities"];
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const [facilityId, setFacilityId] = useState(ALL_FACILITIES_VALUE);
  const [riskType, setRiskType] = useState<RiskType>("all");
  const [sortBy, setSortBy] = useState<SortBy>("daysOpen");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [page, setPage] = useState(1);
  const options: EnterpriseAssetsV2RequestOptions = {
    facilityId: facilityId === ALL_FACILITIES_VALUE ? undefined : facilityId,
    riskType,
    sortBy,
    sortDirection,
    page,
    pageSize: TABLE_PAGE_SIZE,
  };
  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAssetsV2Table(customerId, options, { keepPreviousData: true });

  const rows = data?.highRiskAssets.rows ?? [];
  const table = data?.highRiskAssets;
  if (isPending && !data) {
    return <AssetsV2HighRiskAssetsTableSkeleton />;
  }

  const total = table?.total ?? summary.highRiskAssetCount;
  const totalPages = table?.pageCount ?? 1;
  const start = total === 0 ? 0 : (page - 1) * TABLE_PAGE_SIZE + 1;
  const end = Math.min(page * TABLE_PAGE_SIZE, total);
  const counts = table?.counts ?? {
    all: summary.highRiskAssetCount,
    maintenanceRequired: summary.maintenanceRequiredAssetCount,
    monitoringOffline: summary.monitoringOfflineAssetCount,
  };
  const facilityOptions = [
    {
      label: t("enterprise.assetsV2.allFacilities"),
      value: ALL_FACILITIES_VALUE,
    },
    ...facilities.map((facility) => ({
      label: facility.name,
      value: facility.id,
    })),
  ];
  const percent = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(summary.highRiskAssetPercentage);
  const countDescription = t("enterprise.assetsV2.highRiskTableCount", {
    count: formatAssetsV2Count(summary.highRiskAssetCount, locale),
    percent,
  });

  const columns: DataTableColumnDef<AssetRow>[] = [
    {
      id: "rowNumber",
      header: "#",
      meta: { cellVariant: "starting", width: "5%" },
      cell: ({ row }) => {
        if (isPlaceholderData) {
          return <Skeleton width={20} height={16} />;
        }

        const rowNumber = formatAssetsV2Count(
          (page - 1) * TABLE_PAGE_SIZE + row.index + 1,
          locale,
        );
        return <Text>{rowNumber}</Text>;
      },
    },
    {
      id: "asset",
      accessorFn: (row) => row.assetName ?? "",
      header: t("enterprise.assetsV2.tableColumnAsset"),
      meta: {
        cellVariant: "doubleLine",
        width: "auto",
      },
      cell: ({ row }) => {
        if (isPlaceholderData) {
          return <Skeleton className="w-[72%]" height={16} />;
        }

        const description =
          row.original.summary ?? t("enterprise.assetsV2.summaryUnavailable");
        const assetName = row.original.assetName ?? "—";
        return (
          <TableCellStack>
            <Tooltip label={assetName}>
              <button
                type="button"
                aria-label={assetName}
                className="flex w-full min-w-0 border-0 bg-transparent p-0 text-left"
              >
                <TableCellPrimary>{assetName}</TableCellPrimary>
              </button>
            </Tooltip>
            <Tooltip label={description}>
              <button
                type="button"
                aria-label={description}
                className="flex w-full min-w-0 border-0 bg-transparent p-0 text-left"
              >
                <TableCellSecondary>{description}</TableCellSecondary>
              </button>
            </Tooltip>
          </TableCellStack>
        );
      },
    },
    ...(hasFacilityGroups
      ? ([
          {
            id: "group",
            accessorFn: (row) => row.groupNames.join(", "),
            header: t("enterprise.assetsV2.tableColumnGroup"),
            enableSorting: true,
            meta: { cellVariant: "text" as const, width: "15%" },
            cell: ({ row }) => {
              if (isPlaceholderData) {
                return <Skeleton className="w-[76%]" height={16} />;
              }

              const groupName =
                row.original.groupNames.join(", ") ||
                t("enterprise.assetsV2.unassigned");
              return (
                <Tooltip label={groupName}>
                  <button
                    type="button"
                    aria-label={groupName}
                    className="block w-full min-w-0 border-0 bg-transparent p-0 text-left"
                  >
                    <Text as="div" truncate>
                      {groupName}
                    </Text>
                  </button>
                </Tooltip>
              );
            },
          },
        ] satisfies DataTableColumnDef<AssetRow>[])
      : []),
    {
      accessorKey: "facilityName",
      header: t("enterprise.assetsV2.tableColumnFacility"),
      enableSorting: true,
      meta: { cellVariant: "text", width: "16%" },
      cell: ({ row }) =>
        isPlaceholderData ? (
          <Skeleton className="w-[82%]" height={16} />
        ) : (
          <Tooltip label={row.original.facilityName ?? "—"}>
            <button
              type="button"
              aria-label={row.original.facilityName ?? "—"}
              className="block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap border-0 bg-transparent p-0 text-left"
            >
              {row.original.facilityName ?? "—"}
            </button>
          </Tooltip>
        ),
    },
    {
      accessorKey: "daysOpen",
      header: t("enterprise.assetsV2.tableColumnDaysOpen"),
      enableSorting: true,
      meta: { cellVariant: "text", width: "14%" },
      cell: ({ row }) => {
        if (isPlaceholderData) {
          return <Skeleton width={46} height={16} />;
        }

        const daysOpenLabel =
          row.original.daysOpen === null
            ? t("enterprise.assetsV2.daysOpenUnavailable")
            : t("enterprise.assetsV2.daysValue", {
                count: row.original.daysOpen,
              });
        return daysOpenLabel;
      },
    },
    {
      id: "status",
      header: t("enterprise.assetsV2.tableColumnStatus"),
      meta: { cellVariant: "text", width: 220 },
      cell: ({ row }) =>
        isPlaceholderData ? (
          <Skeleton width={136} height={28} radius="full" />
        ) : (
          <Group gap={4} wrap="wrap">
            {row.original.maintenanceRequired ? (
              <Tooltip label={t("enterprise.assetsV2.tableFilterRecommended")}>
                <button
                  type="button"
                  aria-label={t("enterprise.assetsV2.tableFilterRecommended")}
                  className="inline-flex max-w-full border-0 bg-transparent p-0 text-left"
                >
                  <Chip
                    tone="warning"
                    icon="maintenance"
                    label={t("enterprise.assetsV2.tableFilterRecommended")}
                  />
                </button>
              </Tooltip>
            ) : null}
            {row.original.monitoringOffline ? (
              <Tooltip
                label={t("enterprise.assetsV2.tableFilterMonitoringOffline")}
              >
                <button
                  type="button"
                  aria-label={t(
                    "enterprise.assetsV2.tableFilterMonitoringOffline",
                  )}
                  className="inline-flex max-w-full border-0 bg-transparent p-0 text-left"
                >
                  <Chip
                    tone="neutral"
                    icon="wifi"
                    label={t(
                      "enterprise.assetsV2.tableFilterMonitoringOffline",
                    )}
                  />
                </button>
              </Tooltip>
            ) : null}
          </Group>
        ),
    },
  ];

  function handleSortingChange(
    updater: SortingState | ((previous: SortingState) => SortingState),
  ) {
    const current: SortingState = [
      { id: sortBy, desc: sortDirection === "desc" },
    ];
    const next = typeof updater === "function" ? updater(current) : updater;
    const nextSortBy = next[0]?.id;
    setSortBy(
      nextSortBy === "facilityName" || nextSortBy === "group"
        ? nextSortBy === "group"
          ? "groupName"
          : "facilityName"
        : "daysOpen",
    );
    setSortDirection(next[0]?.desc === false ? "asc" : "desc");
    setPage(1);
  }

  const sorting: SortingState = [
    {
      id: sortBy === "groupName" ? "group" : sortBy,
      desc: sortDirection === "desc",
    },
  ];

  return (
    <Stack gap={12}>
      <Group align="center" justify="between" gap={16} wrap="wrap">
        <Group align="baseline" gap={12} wrap="wrap">
          <Title as="h2" size="lg">
            {t("enterprise.assetsV2.highRiskTableTitle")}
          </Title>
          <Text size="sm" tone="subtle">
            {countDescription}
          </Text>
        </Group>
        <Button variant="outline" size="sm" icon="download" disabled>
          {t("enterprise.assetsV2.exportToExcel")}
        </Button>
      </Group>

      <Group align="center" justify="between" gap={12} wrap="wrap">
        <Group align="center" gap={8} wrap="wrap">
          {(
            [
              ["all", "tableFilterAll", counts.all],
              [
                "maintenanceRequired",
                "tableFilterRecommended",
                counts.maintenanceRequired,
                "maintenance",
                "warning",
              ],
              [
                "monitoringOffline",
                "tableFilterMonitoringOffline",
                counts.monitoringOffline,
                "wifi",
                "secondary",
              ],
            ] as const
          ).map(([value, labelKey, count, iconName, iconTone]) => (
            <Button
              key={value}
              size="sm"
              variant={riskType === value ? "primary" : "outline"}
              aria-pressed={riskType === value}
              onClick={() => {
                setRiskType(value);
                setPage(1);
              }}
              disabled={isPending}
            >
              {iconName ? (
                <Icon name={iconName} size="sm" tone={iconTone} />
              ) : null}
              {t(`enterprise.assetsV2.${labelKey}`)} ·{" "}
              {formatAssetsV2Count(count, locale)}
            </Button>
          ))}
        </Group>
        <div className="w-full sm:w-[240px]">
          <Select
            label={t("enterprise.assetsV2.facilityFilter")}
            size="sm"
            value={facilityId}
            options={facilityOptions}
            disabled={isPending}
            reserveSubtextSpace={false}
            data-testid="assets-v2-facility-filter"
            onChange={(value) => {
              setFacilityId(value ?? ALL_FACILITIES_VALUE);
              setPage(1);
            }}
          />
        </div>
      </Group>

      {isError || (!isPending && !data) ? (
        <Alert
          tone="negative"
          title={t("enterprise.assetsV2.tableLoadErrorTitle")}
          subtext={t("enterprise.assetsV2.loadErrorDescription")}
          action={
            <Button size="sm" onClick={() => void refetch()}>
              {t("enterprise.assetsV2.retry")}
            </Button>
          }
        />
      ) : null}
      {data && !isError ? (
        <div className="max-md:[&_table]:table-auto md:[&_table]:table-fixed">
          <DataTable
            ariaLabel={t("enterprise.assetsV2.highRiskTableTitle")}
            columns={columns}
            data={rows}
            size="md"
            variant="card"
            sorting={sorting}
            onSortingChange={handleSortingChange}
            manualSorting
            manualPagination
            emptyMessage={t("enterprise.assetsV2.tableEmpty")}
            pagination={{
              variant: "simplified",
              currentPage: page,
              totalPages,
              totalItems: total,
              rowsPerPage: TABLE_PAGE_SIZE,
              onPageChange: setPage,
              contextLabel: t("enterprise.assetsV2.tablePagination", {
                start: formatAssetsV2Count(start, locale),
                end: formatAssetsV2Count(end, locale),
                total: formatAssetsV2Count(total, locale),
              }),
            }}
          />
        </div>
      ) : null}
    </Stack>
  );
}
